/**
 * VerificarPorComprobanteModal — verificación de cuenta bancaria por
 * comprobante de asociación.
 *
 * Método alternativo al micro-depósito (política aprobada 2026-08-11):
 * si la transferencia con la que el asociado pagó su asociación salió de
 * la misma cuenta que declaró, es titular probado.
 *
 * El operador ve lado a lado la cuenta declarada y el comprobante de aporte,
 * verifica visualmente cuenta origen + nombre del remitente, e ingresa la
 * referencia (nº de boleta/transacción) de la transferencia entrante ya
 * conciliada. El RPC `verificar_cuenta_por_comprobante` valida, marca la
 * cuenta como verificada y deja audit. Retiros quedan habilitados tras el
 * cooldown de 24h (`habilitada_retiros_at`).
 */
import { useEffect, useState, type FormEvent } from 'react'
import { CheckCircle, Warning } from '@phosphor-icons/react'
import { Modal } from './Modal'
import { ComprobanteAportePanel } from './ComprobanteAportePanel'
import { supabase } from '@/lib/supabase'

export type CuentaParaComprobante = {
  id: string
  banco: string
  tipo_cuenta: string
  numero_cuenta: string
  titular_nombre: string
  asociado_nombre: string
  asociado_no: string
  asociado_dpi: string
  comprobante_aporte_url: string | null
}

type Props = {
  open: boolean
  onClose: () => void
  cuenta: CuentaParaComprobante
  /** Se llama tras verificar con éxito (al cerrar el modal) para refrescar la lista. */
  onSuccess: () => void
}

const fmtFechaHora = (iso: string) =>
  new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Guatemala',
  })

export function VerificarPorComprobanteModal({ open, onClose, cuenta, onSuccess }: Props) {
  const [referencia, setReferencia] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [habilitadaRetirosAt, setHabilitadaRetirosAt] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setReferencia('')
    setError(null)
    setHabilitadaRetirosAt(null)
  }, [open])

  const verificada = habilitadaRetirosAt !== null

  const cerrar = () => {
    if (verificada) onSuccess()
    onClose()
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const ref = referencia.trim()
    if (ref.length < 4) {
      setError('La referencia debe tener al menos 4 caracteres.')
      return
    }

    setSubmitting(true)
    const { data, error: rpcErr } = await supabase.rpc('verificar_cuenta_por_comprobante', {
      p_cuenta_id: cuenta.id,
      p_referencia: ref,
    })
    setSubmitting(false)

    if (rpcErr) {
      setError(`No se pudo verificar: ${rpcErr.message}`)
      return
    }

    const res = data as { ok: boolean; habilitada_retiros_at: string }
    setHabilitadaRetirosAt(res.habilitada_retiros_at)
  }

  return (
    <Modal
      open={open}
      onClose={cerrar}
      title="Verificar por comprobante de asociación"
      subtitle={`${cuenta.asociado_nombre} · ${cuenta.asociado_no}`}
      size="lg"
      blocking={submitting}
      footer={
        verificada ? (
          <button type="button" className="btn btn-primary" onClick={cerrar}>
            Listo
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button
              type="submit"
              form="verificar-comprobante-form"
              className="btn btn-primary"
              disabled={submitting || referencia.trim().length < 4}
            >
              {submitting ? 'Verificando…' : 'Verificar cuenta'}
            </button>
          </>
        )
      }
    >
      {verificada ? (
        <div
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 10, padding: '28px 16px', textAlign: 'center',
          }}
        >
          <CheckCircle size={44} weight="duotone" color="var(--ok-ink, #0f8a57)" />
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink)' }}>
            Cuenta verificada
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-soft, #4b5563)', lineHeight: 1.55, maxWidth: 420 }}>
            La cuenta {cuenta.banco} N° {cuenta.numero_cuenta} quedó verificada por
            comprobante de asociación.
          </div>
          <div
            style={{
              marginTop: 6, padding: '10px 16px', borderRadius: 10,
              background: 'rgba(15,138,87,0.10)', color: 'var(--ok-ink, #0f8a57)',
              fontSize: 13, fontWeight: 700,
            }}
          >
            Retiros habilitados a partir de {fmtFechaHora(habilitadaRetirosAt)}
          </div>
        </div>
      ) : (
        <form
          id="verificar-comprobante-form"
          onSubmit={onSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

          <div
            style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              padding: '12px 14px', borderRadius: 10,
              background: 'rgba(217,119,6,0.08)',
              border: '1px solid rgba(217,119,6,0.25)',
              fontSize: 12, color: 'var(--warning, #92600a)', lineHeight: 1.55,
            }}
          >
            <Warning size={18} weight="duotone" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              <strong style={{ fontWeight: 800 }}>
                Verificá que la transferencia del comprobante salga de ESTA cuenta
                y que el nombre del remitente coincida con el asociado.
              </strong>{' '}
              Si la cuenta origen o el nombre no coinciden, no verifiques por este método.
            </span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(240px, 1fr) minmax(260px, 1fr)',
              gap: 14,
              alignItems: 'start',
            }}
          >
            {/* Columna izquierda: cuenta declarada + asociado */}
            <div
              style={{
                border: '1px solid var(--hairline)', borderRadius: 12,
                padding: '14px 16px',
                display: 'flex', flexDirection: 'column', gap: 2,
              }}
            >
              <div
                style={{
                  fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)',
                  textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6,
                }}
              >
                Cuenta declarada
              </div>
              <Dato label="Banco" value={`${cuenta.banco} · ${cuenta.tipo_cuenta}`} />
              <Dato label="Número de cuenta" value={cuenta.numero_cuenta} mono />
              <Dato label="Titular declarado" value={cuenta.titular_nombre} />
              <div style={{ borderTop: '1px solid var(--line-soft, var(--hairline))', margin: '8px 0' }} />
              <div
                style={{
                  fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)',
                  textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6,
                }}
              >
                Asociado
              </div>
              <Dato label="Nombre completo" value={cuenta.asociado_nombre} />
              <Dato label="No. asociado" value={cuenta.asociado_no} mono />
              <Dato label="DPI" value={cuenta.asociado_dpi} mono />
            </div>

            {/* Columna derecha: comprobante de aporte */}
            <ComprobanteAportePanel comprobantePath={cuenta.comprobante_aporte_url} />
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span
              style={{
                fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)',
                letterSpacing: 1.2, textTransform: 'uppercase',
              }}
            >
              Referencia de la transferencia (nº de boleta/transacción)
            </span>
            <input
              type="text"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Ej. 48273615"
              required
              minLength={4}
              style={{
                padding: '10px 12px', border: '1px solid var(--hairline)',
                borderRadius: 8, fontSize: 13, fontFamily: 'inherit',
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
              El número de la transferencia entrante conciliada con la que el asociado
              pagó su asociación. Mínimo 4 caracteres. Queda en el audit.
            </span>
          </label>
        </form>
      )}
    </Modal>
  )
}

function Dato({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0' }}>
      <span style={{ fontSize: 11, color: 'var(--muted-foreground)', fontWeight: 700 }}>{label}</span>
      <span
        style={{
          fontSize: 13, fontWeight: 600, color: 'var(--ink)', textAlign: 'right',
          fontFamily: mono ? 'IBM Plex Mono, monospace' : undefined,
          fontFeatureSettings: mono ? '"tnum"' : undefined,
        }}
      >
        {value}
      </span>
    </div>
  )
}
